import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "./api";
import {
  getBookmarks,
  getSavedDeepDives,
  getSavedConnections,
  setBookmarks,
  setSavedDeepDives,
  setSavedConnections,
} from "./bookmarks";
import { getSessionId } from "./session";
import { setAdminCode, clearAdmin, getAdminCode } from "./admin";

const LOCAL_TOKEN_KEY = "vault.auth.token";

const AuthContext = createContext({
  user: null,
  ready: false,
  signup: async () => {},
  login: async () => {},
  logout: async () => {},
  recover: async () => {},
});

// Use cookies + bearer fallback
api.defaults.withCredentials = true;
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(LOCAL_TOKEN_KEY);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// MERGE: send local bookmarks+reactions to cloud, then pull the union back into localStorage.
// This is the user's first taste of cross-device sync. We never delete anything on either side.
async function mergeLocalIntoCloud() {
  try {
    const localBookmarks = {
      dossiers: getBookmarks(),
      deepdives: getSavedDeepDives(),
      connections: getSavedConnections(),
    };
    const localTotal =
      (localBookmarks.dossiers?.length || 0) +
      (localBookmarks.deepdives?.length || 0) +
      (localBookmarks.connections?.length || 0);
    const bm = await api
      .post("/me/bookmarks/sync", localBookmarks, { timeout: 10000 })
      .then((r) => r.data)
      .catch(() => null);
    let cloudTotal = 0;
    if (bm) {
      setBookmarks(bm.dossiers || []);
      setSavedDeepDives(bm.deepdives || []);
      setSavedConnections(bm.connections || []);
      cloudTotal =
        (bm.dossiers?.length || 0) +
        (bm.deepdives?.length || 0) +
        (bm.connections?.length || 0);
    }

    // Reactions — claim the anon session and send any local reactions we know about
    // We don't track anon reactions in localStorage, so we just pass session_id so the
    // backend can claim ownership of everything tied to that session.
    const reactionsResp = await api
      .post(
        "/me/reactions/sync",
        { reactions: [], session_id: getSessionId() },
        { timeout: 10000 }
      )
      .then((r) => r.data)
      .catch(() => null);

    // User-visible affordance — confirm sync happened so the silent magic feels real
    const claimedReactions = reactionsResp?.claimed || reactionsResp?.added || 0;
    const newFromCloud = Math.max(0, cloudTotal - localTotal);
    const bits = [];
    if (cloudTotal > 0) bits.push(`${cloudTotal} saved file${cloudTotal === 1 ? "" : "s"}`);
    if (claimedReactions > 0) bits.push(`${claimedReactions} reaction${claimedReactions === 1 ? "" : "s"}`);
    if (bits.length > 0) {
      toast.success("Vault synced", {
        description:
          newFromCloud > 0
            ? `Pulled ${newFromCloud} from cloud · ${bits.join(" · ")} now on this device`
            : `${bits.join(" · ")} on this device`,
        duration: 4000,
      });
    }
  } catch {
    // never block the signin flow
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me", { timeout: 6000 });
      let u = r.data?.user || null;
      // GUARD AGAINST SILENT SIGN-OUT: the backend returns 200 with
      // `{user: null}` on ANY auth failure (cookie missing, token expired,
      // even briefly-flaky cookies during a cold start). If we still have a
      // local token, retry ONCE with an explicit Bearer header before
      // accepting the null. This kills the "I refreshed and got signed out"
      // bug at the source.
      if (!u) {
        const localToken = (() => {
          try { return localStorage.getItem(LOCAL_TOKEN_KEY); } catch { return null; }
        })();
        if (localToken) {
          try {
            const retry = await api.get("/auth/me", {
              timeout: 6000,
              headers: { Authorization: `Bearer ${localToken}` },
            });
            u = retry.data?.user || null;
            // If the retry got a real user, the cookie just wasn't being
            // sent — clear stale state nothing else, the token in localStorage
            // is still our truth.
          } catch (retryErr) {
            // Only an explicit 401 means the token is truly dead. On any
            // other failure (network, 5xx), keep the existing UI state.
            if (retryErr?.response?.status === 401) {
              try { localStorage.removeItem(LOCAL_TOKEN_KEY); } catch { /* ignore */ }
              u = null;
            } else {
              // Network blip — don't sign the user out. Bail out of refresh()
              // entirely so we leave existing user state alone.
              setReady(true);
              return;
            }
          }
        }
      }
      setUser(u);
      // On every fresh page load with an active session, pull the user's cloud
      // bookmarks into localStorage so the saved-files page shows everything.
      if (u) {
        try {
          const bm = await api.get("/me/bookmarks", { timeout: 8000 }).then((x) => x.data);
          if (bm) {
            setBookmarks(bm.dossiers || []);
            setSavedDeepDives(bm.deepdives || []);
            setSavedConnections(bm.connections || []);
          }
        } catch {
          // silent — keep local data
        }
        // Daily login coin award — server-side daily cap of 10 means it can
        // only award once per UTC day even if this fires on every refresh.
        try {
          const er = await api.post("/coins/earn", { event_type: "daily_login" });
          if (er.data?.awarded > 0) {
            window.dispatchEvent(new Event("coins-changed"));
          }
        } catch {
          // silent
        }
        // Admin decoupled from user accounts (2026-02). The frontend used to
        // auto-grant admin if the signed-in user had is_owner=true. That
        // coupling caused churn between "admin user" and "regular user"
        // states and complicated logout. Admin is now exclusively granted
        // by visiting /admin and entering the access code (stored in
        // localStorage). Anyone who hasn't done that = not admin. Period.
      }
      // Note: we DO NOT clear admin code on sign-out anymore. The admin code
      // is its own session, independent of user login. To exit admin mode,
      // visit /admin and click Sign Out (which calls clearAdmin()) — or
      // clear localStorage manually.
    } catch (e) {
      // ONLY clear the session on an explicit 401 (token actually invalid).
      // Network blips, timeouts (a slow tap onto /profile while the page is
      // still loading), or any 5xx must NOT wipe the user — that was causing
      // people to be silently signed out when they navigated to the profile.
      const status = e?.response?.status;
      if (status === 401) {
        setUser(null);
      }
      // Otherwise leave the existing user state alone.
    } finally {
      setReady(true);
    }
  }, []);

  // Lightweight refetch of just /auth/me so consumers (Navbar, ID card) can
  // pick up profile changes (e.g. avatar swap) without re-pulling bookmarks.
  const refreshUser = useCallback(async () => {
    try {
      const r = await api.get("/auth/me", { timeout: 6000 });
      setUser(r.data?.user || null);
    } catch {
      // keep existing user
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // External refresh trigger — components like InlineAvatarPicker dispatch
  // `vault:user-changed` after they mutate /users/me so the navbar pill
  // + ID card update without a page reload.
  useEffect(() => {
    const onChanged = () => { refreshUser(); };
    window.addEventListener("vault:user-changed", onChanged);
    return () => window.removeEventListener("vault:user-changed", onChanged);
  }, [refreshUser]);

  // Presence heartbeat — while the user is signed in and the tab is visible,
  // ping /me/presence every 60s so other people see the green "active" dot
  // next to their avatar. Backend tolerates frequent calls; the toggle to
  // suppress this is on the server (show_active_status preference).
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const ping = () => {
      if (document.visibilityState !== "visible") return;
      api.post("/me/presence").catch(() => { /* offline / 401 — ignore */ });
    };
    ping();                                     // immediate ping on mount
    const id = setInterval(() => { if (!cancelled) ping(); }, 60000);
    const onVis = () => ping();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);

  const signup = useCallback(async (username, password) => {
    const r = await api.post("/auth/signup", { username, password });
    if (r.data?.token) localStorage.setItem(LOCAL_TOKEN_KEY, r.data.token);
    setUser(r.data?.user || null);
    await mergeLocalIntoCloud();
    return r.data; // includes recovery_code (once)
  }, []);

  const login = useCallback(async (username, password) => {
    const r = await api.post("/auth/login", { username, password });
    if (r.data?.token) localStorage.setItem(LOCAL_TOKEN_KEY, r.data.token);
    setUser(r.data?.user || null);
    await mergeLocalIntoCloud();
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    localStorage.removeItem(LOCAL_TOKEN_KEY);
    // Clear local bookmarks too — otherwise the next person who signs in on
    // this device would inherit this user's saves on the next merge.
    setBookmarks([]);
    setSavedDeepDives([]);
    setSavedConnections([]);
    // Always drop the admin code so the next user on this device doesn't
    // inherit admin powers.
    clearAdmin();
    setUser(null);
  }, []);

  // Rename the signed-in account. Returns the new user payload on success.
  const changeUsername = useCallback(async (newUsername, currentPassword) => {
    const r = await api.post("/auth/change-username", {
      new_username: newUsername,
      current_password: currentPassword,
    });
    if (r.data?.token) localStorage.setItem(LOCAL_TOKEN_KEY, r.data.token);
    setUser(r.data?.user || null);
    return r.data;
  }, []);

  const recover = useCallback(async (username, recoveryCode, newPassword) => {
    const r = await api.post("/auth/recovery", {
      username,
      recovery_code: recoveryCode,
      new_password: newPassword,
    });
    if (r.data?.token) localStorage.setItem(LOCAL_TOKEN_KEY, r.data.token);
    setUser(r.data?.user || null);
    await mergeLocalIntoCloud();
    return r.data; // includes new_recovery_code
  }, []);

  return (
    <AuthContext.Provider value={{ user, ready, signup, login, logout, recover, refreshUser, changeUsername }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function isAuthenticated() {
  return !!localStorage.getItem(LOCAL_TOKEN_KEY);
}

